import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ExternalLink, Shield, Phone, ArrowLeft, CheckCircle2, ArrowRight, Users, DollarSign, Clock } from "lucide-react";

const LifePage = () => {
  const partnerUrl = "https://www.insurenowdirect.com/bradleygreenberg";

  const benefits = [
    {
      title: "Protect Your Family",
      description: "Ensure your loved ones are financially secure if something happens to you.",
      icon: Users,
    },
    {
      title: "Affordable Rates",
      description: "Lock in low rates while you're young and healthy. Rates don't increase.",
      icon: DollarSign,
    },
    {
      title: "Flexible Terms",
      description: "Choose coverage from 10 to 30 years to match your financial goals.",
      icon: Clock,
    },
    {
      title: "Peace of Mind",
      description: "Know that your family's future is protected no matter what happens.",
      icon: Shield,
    },
  ];

  const coverageOptions = [
    { amount: "$100,000", price: "$8", term: "10 years" },
    { amount: "$250,000", price: "$15", term: "20 years", popular: true },
    { amount: "$500,000", price: "$25", term: "20 years" },
    { amount: "$1,000,000", price: "$45", term: "30 years" },
  ];

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Heart className="w-6 h-6" />
              <div>
                <h1 className="font-bold text-lg">Term Life Insurance</h1>
                <p className="text-amber-100 text-sm">Powered by InsureNow Direct</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-navy-600" />
              <a href="tel:201-227-6413" className="hover:text-navy-600">Need help? Call 201.227.6413</a>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span>My Insurance Coverage - Licensed Agent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                Term Life Insurance
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
                Protect the People
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-600">
                  You Love Most
                </span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                We've partnered with InsureNow Direct to bring you affordable term life insurance with no medical exam required for coverage up to $500,000.
              </p>
              <a
                href={partnerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg shadow-amber-500/30"
                data-testid="life-get-quote"
              >
                Get Your Free Quote
                <ExternalLink className="w-5 h-5" />
              </a>
              <p className="text-sm text-slate-500 mt-3">Opens InsureNow Direct in a new tab</p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-amber-100 to-amber-200 rounded-3xl transform -rotate-2"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-6 border border-amber-100">
                <h3 className="text-lg font-bold text-navy-900 mb-4">Quick Coverage Estimate</h3>
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4">
                  <p className="text-sm text-amber-700 mb-1">Recommended Coverage</p>
                  <p className="text-3xl font-bold text-navy-900">$250,000</p>
                  <p className="text-sm text-slate-500 mt-1">Based on 10x annual income</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-slate-500">Starting at</p>
                    <p className="text-2xl font-bold text-navy-900">$8/mo</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-slate-500">No exam up to</p>
                    <p className="text-2xl font-bold text-navy-900">$500K</p>
                  </div>
                </div>
                <a
                  href={partnerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-amber-500 text-white text-center font-semibold rounded-xl hover:bg-amber-600 transition-colors"
                >
                  See Your Rate →
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center">Why Term Life Insurance?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-semibold text-navy-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Options */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-navy-900 mb-2 text-center">Sample Rates</h3>
          <p className="text-slate-600 text-center mb-8">Rates for healthy 30-year-old non-smoker</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coverageOptions.map((option, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-2xl border-2 transition-all ${
                  option.popular
                    ? "border-amber-400 bg-amber-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {option.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-400 text-navy-900 text-xs font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <p className="text-2xl font-bold text-navy-900 mb-1">{option.amount}</p>
                <p className="text-slate-500 text-sm mb-3">{option.term} term</p>
                <p className="text-xl font-bold text-amber-600 mb-4">
                  {option.price}<span className="text-sm font-normal text-slate-500">/mo</span>
                </p>
                <a
                  href={partnerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full py-2 text-center font-semibold rounded-lg transition-colors ${
                    option.popular
                      ? "bg-amber-500 text-white hover:bg-amber-600"
                      : "bg-slate-100 text-navy-700 hover:bg-slate-200"
                  }`}
                >
                  Get Quote
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-400 mt-6">
            * Actual rates may vary based on health, age, and other factors.
          </p>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to Protect Your Family?</h3>
          <p className="text-amber-100 mb-6 max-w-xl mx-auto">
            Get a free quote in just 2 minutes. No medical exam required for coverage up to $500,000.
          </p>
          <a
            href={partnerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-navy-900 text-white font-bold rounded-full hover:bg-navy-800 transition-colors"
          >
            Get Your Free Quote
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        {/* Contact Card */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-navy-600" />
              </div>
              <div>
                <h4 className="font-semibold text-navy-900">Prefer to Speak with Bradley?</h4>
                <p className="text-sm text-slate-600">Your licensed insurance expert is ready to help</p>
              </div>
            </div>
            <a
              href="tel:201-227-6413"
              className="px-6 py-3 bg-navy-600 text-white font-semibold rounded-full hover:bg-navy-700 transition-colors"
            >
              Call 201.227.6413
            </a>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-slate-100 border-t border-slate-200 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">
            Life insurance quotes provided by InsureNow Direct, a licensed insurance partner.
            <br />
            <span className="text-slate-400">
              By using this service, you agree to InsureNow Direct's terms and privacy policy.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LifePage;
